import { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';

/** "Ayşe Yılmaz" → "AY" — dış avatar servisine istek atmadan kimlik göstergesi */
const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toLocaleUpperCase('tr-TR');

const testimonials = [
  {
    name: 'Ayşe Yılmaz',
    role: 'Daire Sahibi, 3+1',
    quote:
      'Rain Nova\'da yaşamaya başlayalı bir yıl oldu. Hem mimari hem de sosyal donatılar beklediğimden çok daha iyi. Çocuklarım için güvenli bir ortam.',
    rating: 5,
  },
  {
    name: 'Mehmet Demir',
    role: 'Yatırımcı',
    quote:
      'Üç farklı projede karşılaştırma yaptım. Rain Nova konum, fiyat ve kalite üçgeninde açık ara öne çıktı. Yatırım açısından çok memnunum.',
    rating: 5,
  },
  {
    name: 'Selin Kaya',
    role: 'Daire Sahibi, 2+1',
    quote:
      'Satış sürecinden teslimine kadar her aşamada şeffaf ve profesyonel bir ekiple çalıştık. Daire kalitesi gerçekten lüks segmentte.',
    rating: 5,
  },
  {
    name: 'Burak Şahin',
    role: 'Mimari Danışman',
    quote:
      'Bölgedeki en başarılı projelerden biri. Detaylar, malzeme seçimi ve peyzaj çözümleri uluslararası standartlarda.',
    rating: 5,
  },
];

export function Testimonials() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, []);

  const t = testimonials[index];

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-br from-slate-950 to-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.05),transparent_60%)]" />
      <div className="container mx-auto px-4 lg:px-8 relative">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 text-white/80 rounded-full text-sm tracking-[0.2em] mb-4">
            REFERANSLAR
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Sakinlerimiz{' '}
            <span className="text-stone-300">
              Ne Diyor?
            </span>
          </h2>
        </m.div>

        <div className="max-w-3xl mx-auto relative">
          <Quote className="absolute -top-6 -left-2 text-stone-500/30" size={80} />

          <AnimatePresence mode="wait">
            <m.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12 text-center"
            >
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} size={16} className="fill-white text-white" />
                ))}
              </div>
              <p className="text-xl md:text-2xl text-gray-100 leading-relaxed mb-8 italic">
                "{t.quote}"
              </p>
              <div className="flex items-center justify-center gap-4">
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 w-14 h-14 rounded-full border-2 border-white/30 bg-white/10 flex items-center justify-center text-lg font-semibold tracking-wide"
                >
                  {initials(t.name)}
                </span>
                <div className="text-left">
                  <div className="font-semibold text-lg">{t.name}</div>
                  <div className="text-sm text-stone-300">{t.role}</div>
                </div>
              </div>
            </m.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={() => setIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Önceki"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex">
              {testimonials.map((item, i) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`${item.name} yorumunu göster`}
                  aria-current={i === index}
                  // Aktif çubuk 32px; kutusu 40px olmazsa flex içinde 24px'e sıkışır
                  className={`flex items-center justify-center h-6 ${i === index ? 'w-10' : 'w-6'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`block h-2 rounded-full transition-all flex-shrink-0 ${
                      i === index ? 'w-8 bg-white' : 'w-2 bg-white/30'
                    }`}
                  />
                </button>
              ))}
            </div>
            <button
              onClick={() => setIndex((i) => (i + 1) % testimonials.length)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Sonraki"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
